import React, { useMemo, useRef, useState, useCallback } from 'react';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
  TooltipItem,
} from 'chart.js';
import { Bubble } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'primeicons/primeicons.css';
import { MedicalSupplyCompaniesData } from 'Interfaces/ProductWikiInterfaces/opportunityAtAGlance.types';

ChartJS.register(LinearScale, PointElement, Tooltip, Legend, zoomPlugin);

// Helper interface for react-chartjs-2 chart ref that can be accessed in different ways
interface ChartRefWrapper {
  chartInstance?: ChartJS;
  chart?: ChartJS;
  getChart?: () => ChartJS;
  isZoomedOrPanned?: () => boolean;
  resetZoom?: () => void;
}

interface BubbleDataPoint {
  x: number;
  y: number;
  r: number;
}

interface BubbleChartProps {
  data?: MedicalSupplyCompaniesData | null;
  isLoading?: boolean;
}

// Official Brand Color Palette for bubble chart
// Colors from official brand style guide (Pantone)
const BUBBLE_COLORS = [
  { bg: 'rgba(82, 45, 110, 0.7)', border: 'rgba(82, 45, 110, 1)' }, // Brand Purple (Pantone 269 C) - #522D6E
  { bg: 'rgba(199, 146, 19, 0.7)', border: 'rgba(199, 146, 19, 1)' }, // Brand Gold (Pantone 1245 C) - #C79213
  { bg: 'rgba(2, 153, 153, 0.7)', border: 'rgba(2, 153, 153, 1)' }, // Brand Teal - #029999
  { bg: 'rgba(153, 7, 153, 0.7)', border: 'rgba(153, 7, 153, 1)' }, // Brand Magenta - #990799
  { bg: 'rgba(120, 86, 135, 0.7)', border: 'rgba(120, 86, 135, 1)' }, // Brand Light Purple - #785687
  { bg: 'rgba(123, 75, 159, 0.7)', border: 'rgba(123, 75, 159, 1)' }, // Secondary Purple - #7B4B9F
  { bg: 'rgba(77, 46, 109, 0.7)', border: 'rgba(77, 46, 109, 1)' }, // Primary Light - #4d2e6d
  { bg: 'rgba(233, 233, 242, 0.9)', border: 'rgba(233, 233, 242, 1)' }, // Brand Light Gray - #E9E9F2
];

/**
 * Calculate bubble radius based on volume share percentage
 * Uses a scaling function to make bubbles visually distinguishable
 */
const calculateBubbleRadius = (volumeSharePct: number): number => {
  // Minimum radius of 8, maximum of 50
  // Scale based on square root of volume share for better visual representation
  const minRadius = 8;
  const maxRadius = 50;

  // Use sqrt scaling for better visual representation of area
  const scaled = Math.sqrt(volumeSharePct) * 5;
  return Math.max(minRadius, Math.min(maxRadius, scaled));
};

const BubbleChart: React.FC<BubbleChartProps> = ({ data, isLoading = false }) => {
  // Zoom state for bubble chart
  const [isBubbleChartZoomed, setIsBubbleChartZoomed] = useState<boolean>(false);
  const bubbleChartRef = useRef<ChartJS | null>(null);

  // Transform API data to Chart.js format
  const { chartData, axisLimits, maxBubbleRadius } = useMemo(() => {
    if (!data?.data || data.data.length === 0) {
      return {
        chartData: { datasets: [] } as ChartData<'bubble', BubbleDataPoint[]>,
        axisLimits: { xMin: 0, xMax: 1, yMin: 0, yMax: 100000 },
        maxBubbleRadius: 8,
      };
    }

    // Calculate axis limits based on data
    const prices = data.data.map(d => d.averagePrice);
    const revenues = data.data.map(d => d.annualRevenue);
    const bubbleRadii = data.data.map(d => calculateBubbleRadius(d.annualVolumeSharePct));

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const minRevenue = Math.min(...revenues);
    const maxRevenue = Math.max(...revenues);
    const maxRadius = Math.max(...bubbleRadii);

    // Calculate dynamic padding based on bubble size
    // We need to convert pixel radius to data units for proper padding
    // Estimate: assume chart is ~400px wide and ~350px tall for calculations
    const chartWidth = 400;
    const chartHeight = 350;

    const priceRange = maxPrice - minPrice || maxPrice * 0.5;
    const revenueRange = maxRevenue - minRevenue || maxRevenue * 0.5;

    // Calculate how much data padding we need for the largest bubble
    // Add extra 20% margin on top of the bubble radius
    const bubblePaddingFactor = 1.3;
    const xPaddingFromBubble = (maxRadius / chartWidth) * priceRange * bubblePaddingFactor;
    const yPaddingFromBubble = (maxRadius / chartHeight) * revenueRange * bubblePaddingFactor;

    // Use at least 15% padding or bubble-based padding, whichever is larger
    const basePricePadding = priceRange * 0.15;
    const baseRevenuePadding = revenueRange * 0.15;

    const pricePadding = Math.max(basePricePadding, xPaddingFromBubble);
    const revenuePadding = Math.max(baseRevenuePadding, yPaddingFromBubble);

    const axisLimits = {
      xMin: Math.max(0, minPrice - pricePadding),
      xMax: maxPrice + pricePadding,
      yMin: Math.max(0, minRevenue - revenuePadding),
      yMax: maxRevenue + revenuePadding,
    };

    // Create datasets for each company
    const datasets = data.data.map((company, index) => {
      const colorIndex = index % BUBBLE_COLORS.length;
      const color = BUBBLE_COLORS[colorIndex];

      return {
        label: company.labelerShortName,
        data: [
          {
            x: company.averagePrice,
            y: company.annualRevenue,
            r: calculateBubbleRadius(company.annualVolumeSharePct),
          },
        ],
        backgroundColor: color.bg,
        borderColor: color.border,
        borderWidth: 2,
        // Store additional data for tooltip
        volumeSharePct: company.annualVolumeSharePct,
        annualVolume: company.annualVolume,
      };
    });

    return {
      chartData: { datasets } as ChartData<'bubble', BubbleDataPoint[]>,
      axisLimits,
      maxBubbleRadius: maxRadius,
    };
  }, [data]);

  // Dynamic chart options based on data
  const options: ChartOptions<'bubble'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      // Prevent bubbles from being clipped at the edges
      clip: false,
      layout: {
        // Add padding around the chart area to accommodate bubbles at edges
        padding: {
          top: Math.max(20, maxBubbleRadius * 0.6),
          right: Math.max(20, maxBubbleRadius * 0.6),
          bottom: 10,
          left: 10,
        },
      },
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            color: '#374151',
            font: { size: 11 },
            boxWidth: 12,
            padding: 10,
          },
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          titleColor: '#374151',
          bodyColor: '#374151',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          padding: 12,
          callbacks: {
            label: (ctx: TooltipItem<'bubble'>) => {
              interface BubbleDatasetWithMeta {
                label?: string;
                volumeSharePct?: number;
                annualVolume?: number;
              }
              const dataset = ctx.dataset as BubbleDatasetWithMeta;
              const label = dataset.label || '';
              const x = ctx.parsed.x;
              const y = ctx.parsed.y;
              const volShare = dataset.volumeSharePct?.toFixed(2) || '0.00';
              const volume = dataset.annualVolume?.toLocaleString() || '0';

              return [
                `${label}`,
                `Avg Price: $${x ? x.toFixed(4) : '0.0000'}`,
                `Revenue: $${y ? y.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}`,
                `Volume: ${volume} units`,
                `Vol Share: ${volShare}%`,
              ];
            },
          },
        },
        zoom: {
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: 'xy' as const, // Both axes zoom
            scaleMode: 'xy' as const, // Both axes scale mode
          },
          pan: {
            enabled: true,
            mode: 'xy' as const, // Both axes pan
          },
          limits: {
            x: {
              min: axisLimits.xMin,
              max: axisLimits.xMax,
            },
            y: {
              min: axisLimits.yMin,
              max: axisLimits.yMax,
            },
          },
          onZoom: (_context: { chart: ChartJS }) => {
            // Set zoomed state immediately when zoom occurs
            setIsBubbleChartZoomed(true);
          },
          onZoomComplete: (context: { chart: ChartJS }) => {
            // Verify zoom state after zoom completes
            if (context?.chart) {
              const chartInstance = context.chart;
              if (typeof chartInstance.isZoomedOrPanned === 'function') {
                const isZoomed = chartInstance.isZoomedOrPanned();
                setIsBubbleChartZoomed(isZoomed);
              } else {
                setIsBubbleChartZoomed(true);
              }
            } else {
              // Fallback: check using ref
              setTimeout(() => {
                checkBubbleChartZoomState();
              }, 100);
            }
          },
          onPan: (_context: { chart: ChartJS }) => {
            // Also show reset button when panning
            setIsBubbleChartZoomed(true);
          },
          onPanComplete: (context: { chart: ChartJS }) => {
            // Check zoom/pan state after pan completes
            if (context?.chart) {
              const chartInstance = context.chart;
              if (typeof chartInstance.isZoomedOrPanned === 'function') {
                const isZoomed = chartInstance.isZoomedOrPanned();
                setIsBubbleChartZoomed(isZoomed);
              } else {
                setIsBubbleChartZoomed(true);
              }
            } else {
              // Fallback: check using ref
              setTimeout(() => {
                checkBubbleChartZoomState();
              }, 100);
            }
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Average Price ($)',
            color: '#374151',
            font: { size: 15, weight: 'bold' },
          },
          ticks: {
            color: '#374151',
            callback: (val: string | number) => '$' + Number(val).toFixed(4),
            font: { size: 15 },
          },
          grid: { color: '#e5e7eb' },
          min: axisLimits.xMin,
          max: axisLimits.xMax,
        },
        y: {
          title: {
            display: true,
            text: 'Annual Revenue ($)',
            color: '#374151',
            font: { size: 15, weight: 'bold' },
          },
          ticks: {
            color: '#374151',
            callback: (val: string | number) => {
              const num = Number(val);
              if (num >= 1000000) return '$' + (num / 1000000).toFixed(1) + 'M';
              if (num >= 1000) return '$' + (num / 1000).toFixed(0) + 'K';
              return '$' + num.toLocaleString();
            },
            font: { size: 15 },
          },
          grid: { color: '#e5e7eb' },
          min: axisLimits.yMin,
          max: axisLimits.yMax,
        },
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [axisLimits, maxBubbleRadius]
  );

  // Helper function to check if bubble chart is zoomed or panned
  const checkBubbleChartZoomState = useCallback(() => {
    const chartRef = bubbleChartRef.current;
    if (chartRef) {
      // react-chartjs-2 v5: chart instance is available through different paths
      const chartInstance = 
        (chartRef as unknown as ChartRefWrapper).chartInstance || 
        (chartRef as unknown as ChartRefWrapper).chart || 
        (chartRef as unknown as ChartRefWrapper).getChart?.() ||
        chartRef;
      
      if (chartInstance && typeof chartInstance.isZoomedOrPanned === 'function') {
        try {
          const isZoomed = chartInstance.isZoomedOrPanned();
          setIsBubbleChartZoomed(isZoomed);
          return isZoomed;
        } catch (error) {
          console.warn('Error checking bubble chart zoom state:', error);
        }
      }
    }
    return false;
  }, []);

  // Reset zoom function for bubble chart
  // Uses chartjs-plugin-zoom API: chart.resetZoom(mode = 'none'): void
  // Resets the current chart bounds to the defaults that were used before any zooming or panning occurred
  const resetBubbleChartZoom = useCallback(() => {
    const chartRef = bubbleChartRef.current;
    if (chartRef) {
      // react-chartjs-2: access chart instance through the ref
      // The chart instance is available directly or through chartInstance property
      const chartInstance = (chartRef as unknown as ChartRefWrapper).chartInstance || (chartRef as unknown as ChartRefWrapper).chart || chartRef;
      
      if (chartInstance && typeof chartInstance.resetZoom === 'function') {
        // Call resetZoom() to reset chart bounds to defaults before zooming/panning
        // Using default mode 'none' for immediate reset without animation
        chartInstance.resetZoom();
        
        // Update zoom state - check if still zoomed after reset
        // Use isZoomedOrPanned() API method if available
        if (typeof chartInstance.isZoomedOrPanned === 'function') {
          setIsBubbleChartZoomed(chartInstance.isZoomedOrPanned());
        } else {
          // Fallback: assume reset was successful
          setIsBubbleChartZoomed(false);
        }
      }
    }
  }, []);

  // Consistent card styling matching other chart cards
  const cardStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, rgba(249, 250, 251, 0.8), rgba(243, 244, 246, 0.8))',
    backdropFilter: 'blur(10px)',
    padding: '1.5rem',
    borderRadius: '12px',
    border: '1px solid rgba(82, 45, 110, 0.1)',
    boxShadow: '0 4px 16px rgba(82, 45, 110, 0.08)',
    display: 'flex',
    flexDirection: 'column',
  };

  const innerContentStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7))',
    backdropFilter: 'blur(5px)',
    borderRadius: '8px',
    border: '1px solid rgba(82, 45, 110, 0.1)',
    boxShadow: '0 4px 16px rgba(82, 45, 110, 0.08)',
    padding: '1rem',
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  // Loading state
  if (isLoading) {
    return (
      <div style={cardStyle}>
        <h4
          style={{
            color: '#374151',
            fontSize: '1.5rem',
            fontWeight: '500',
            marginBottom: '1rem',
            margin: 0,
          }}
        >
          Medical Supply Companies
        </h4>
        <div style={{ ...innerContentStyle, minHeight: '400px' }}>
          <div style={{ textAlign: 'center', color: '#6b7280' }}>
            <div className="spinner" style={{ marginBottom: '8px' }}>
              Loading...
            </div>
            <p>Loading medical supply companies data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data?.data || data.data.length === 0) {
    return (
      <div style={cardStyle}>
        <h4
          style={{
            color: '#374151',
            fontSize: '1.5rem',
            fontWeight: '500',
            marginBottom: '1rem',
            margin: 0,
          }}
        >
          Medical Supply Companies
        </h4>
        <div style={{ ...innerContentStyle, minHeight: '400px' }}>
          <div style={{ textAlign: 'center', color: '#6b7280' }}>
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>No Data Available</p>
            <p style={{ fontSize: '14px' }}>
              No medical supply companies data found for this product.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <h4
          style={{
            color: '#374151',
            fontSize: '1.5rem',
            fontWeight: '500',
            margin: 0,
          }}
        >
          Medical Supply Companies
        </h4>
        <p style={{ color: '#6b7280', fontSize: '1rem', margin: 0, fontWeight: '700' }}>
          X: Avg Price | Y: Revenue | Bubble Size: Volume Share %
        </p>
      </div>
      <div
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7))',
          backdropFilter: 'blur(5px)',
          borderRadius: '8px',
          border: '1px solid rgba(82, 45, 110, 0.1)',
          boxShadow: '0 4px 16px rgba(82, 45, 110, 0.08)',
          padding: '1rem',
          flex: 1,
        }}
      >
        <div style={{ height: '460px', position: 'relative' }}>
          {' '}
          {/* Increased height for better data visibility */}
          <Bubble ref={bubbleChartRef} options={options} data={chartData} />
          {isBubbleChartZoomed && (
            <button
              onClick={resetBubbleChartZoom}
              aria-label="Reset zoom"
              title="Reset zoom"
              style={{
                position: 'absolute',
                bottom: '1rem',
                left: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                border: '1px solid #d1d5db',
                background: 'white',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                color: '#374151',
                fontSize: '0.875rem',
                fontWeight: 500,
                zIndex: 100,
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                transition: 'all 0.2s ease-in-out',
                pointerEvents: 'auto',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f9fafb';
                e.currentTarget.style.borderColor = '#522D6E';
                e.currentTarget.style.color = '#522D6E';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.color = '#374151';
                e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = 'scale(0.98)';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <i className="pi pi-refresh" style={{ fontSize: '0.875rem' }}></i>
              Reset Zoom
            </button>
          )}
        </div>
      </div>
      {/* Summary stats */}
      {/* <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '2rem',
          marginTop: '1rem',
          paddingTop: '1rem',
          borderTop: '1px solid rgba(82, 45, 110, 0.1)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Total Volume
          </span>
          <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#374151' }}>
            {data.totalVolume.toLocaleString()}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Total Revenue
          </span>
          <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#374151' }}>
            ${data.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Companies
          </span>
          <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#374151' }}>
            {data.data.length}
          </span>
        </div>
      </div> */}
    </div>
  );
};

export default BubbleChart;
